import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { TALENT as DEFAULT_TALENT } from "@/data/talent";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const figureFrame = "relative bg-[#f4f4f4] border border-[#e5e5e5] overflow-hidden";
const captionClass =
  "absolute bottom-4 left-4 text-[11px] font-mono uppercase tracking-[.12em] text-[#666666] bg-white/90 px-3 py-1.5 border border-[#e5e5e5]";

const TalentProfilePage = () => {
  const { slug } = useParams();
  const [talent, setTalent] = useState(DEFAULT_TALENT.find((t) => t.slug === slug) || null);

  useEffect(() => {
    axios.get(`${API}/talent/${slug}`).then((res) => setTalent(res.data)).catch(() => {});
  }, [slug]);

  if (!talent) {
    return (
      <section className="bg-white text-[#121212] py-24 md:py-32" data-testid="talent-not-found">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="font-disp text-[clamp(32px,4vw,56px)] leading-[1.05] tracking-tight font-normal text-[#121212] mb-6">
            Profile not found.
          </h1>
          <Link
            to="/our-people"
            data-testid="talent-back-link"
            className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020] transition-colors"
          >
            Back to Our People →
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <Seo
        title={`${talent.name} — #whatshesaidproject`}
        description={talent.focus || `${talent.name}, featured talent at #whatshesaidproject.`}
        path={`/our-people/${talent.slug}`}
      />
      {/* PROFILE HERO */}
      <section className="bg-white text-[#121212] pt-12 pb-24 md:py-28 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <Link
            to="/our-people"
            data-testid="talent-back-link"
            className="text-xs font-mono uppercase tracking-[.14em] text-[#555555] hover:text-[#e02020] transition-colors inline-block mb-8"
          >
            ← Our People
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <figure className={`${figureFrame} aspect-[4/5]`}>
                <img
                  src={talent.img}
                  alt={talent.caption}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <figcaption className={captionClass}>{talent.caption}</figcaption>
              </figure>
            </div>
            <div className="lg:col-span-7 min-w-0">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                Featured Talent
              </span>
              <h1
                data-testid="talent-name"
                className="font-disp text-[clamp(36px,5vw,64px)] leading-[1.02] tracking-tight font-normal text-[#121212] mb-3"
              >
                {talent.name}
              </h1>
              <p className="text-xs font-mono uppercase tracking-[.14em] text-[#555555] mb-8">
                {talent.role}
              </p>
              <p className="text-[17px] leading-[1.65] text-[#333333] mb-6">{talent.focus}</p>
              <p className="text-sm text-[#555555] leading-relaxed mb-10">
                Full biography, program descriptions, and booking materials will be published here
                ahead of launch.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to={`/connect?talent=${encodeURIComponent(talent.slug)}`}
                  data-testid="talent-book-link"
                  className="inline-flex items-center justify-center px-6 py-3.5 bg-[#121212] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-[#e02020] transition-colors rounded-none"
                >
                  Start a Booking Inquiry →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERTISE */}
      <section className="bg-[#fafafa] text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                Areas of Expertise
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212]">
                Where the work lives.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {talent.expertise.map((area) => (
                  <li
                    key={area}
                    className="flex items-center gap-3 text-xs font-mono uppercase tracking-[.14em] text-[#444444] border-b border-[#e5e5e5] pb-4"
                  >
                    <span className="w-2 h-2 bg-[#e02020]" aria-hidden="true" />
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-white text-[#121212] py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-2">
              Available Programs
            </span>
            <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212] mb-4">
              Keynotes, workshops, and conversations.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {["Keynotes", "Workshops & Training", "Facilitated Conversations"].map((label) => (
              <div
                key={label}
                data-testid={`program-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="border border-[#e5e5e5] bg-[#fafafa] p-8"
              >
                <h3 className="font-disp text-xl text-[#121212] mb-3">{label}</h3>
                <p className="text-xs font-mono uppercase tracking-[.14em] text-[#6b6b6b]">
                  Program details forthcoming
                </p>
              </div>
            ))}
          </div>
          <div className="pt-12">
            <Link
              to="/connect"
              data-testid="talent-cta-connect"
              className="inline-flex items-center justify-center px-6 py-3.5 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-[#121212] transition-colors rounded-none"
            >
              Start a Booking Inquiry →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default TalentProfilePage;
